import { accountIsDisabled } from '@/composables/useAccountEnabled'
import { canSeeBlockedPage, isBlockedPage } from '@/router/accessGates'
import { capabilityRedirect, updateTitle } from '@/router/helpers'
import '@/router/types'
import useAuthStore from '@/stores/auth'
import useCustomAssetAttributeDefinitionsStore from '@/stores/customAssetAttributeDefinitions.store'
import getEnv from '@/util/env'
import { UserIcon } from '@lucide/vue'
import { defineAsyncComponent } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'

const AllCommunicationsViz = () =>
  import('@/views/All/AllCommunicationsViz.vue')
const AnvilView = () => import('@/views/AnvilView.vue')
const APIPlaygroundView = () => import('@/views/APIPlaygroundView.vue')
const AssetsListView = () => import('@/views/Assets/AssetsListView.vue')
const AssetDetailWrapper = () => import('@/views/Assets/AssetDetailView.vue')
const AssetDetailsTab = () => import('@/views/Assets/AssetDetailDefaultTab.vue')
const AssetCommsView = () => import('@/views/Assets/AssetCommunicationsTab.vue')
const AssetCommsTimelineView = () =>
  import('@/views/Assets/AssetCommsTimelineView.vue')
const AssetServicesView = () => import('@/views/Assets/AssetServicesTab.vue')
const AssetFindingsView = () => import('@/views/Assets/AssetFindingsTab.vue')
const AssetVisualizationView = () =>
  import('@/components/dataVisualization/AssetConnectionGraph.vue')
const AssetLapseTimelineView = () =>
  import('@/components/dataVisualization/CommunicationLapseTimeline.vue')
const AssetUptimeHistoryView = () =>
  import('@/views/Assets/AssetUptimeHistoryView.vue')

const AssetProcessesView = () => import('@/views/Assets/AssetProcessesTab.vue')
const AssetProcessExecutionsForProcess = () =>
  import('@/views/Assets/AssetProcessExecutionsForProcess.vue')
const ProcessTreeView = () => import('@/views/ProcessTreeView.vue')

const Dashboard = () => import('@/views/Dashboard.vue')
const DetailedFinding = () => import('@/views/FindingDetailView.vue')
const Findings = () => import('@/views/FindingsListView.vue')
const FindingsNetworkEventsTab = () =>
  import('@/views/FindingsNetworkEventsTab.vue')
// import Hosts from "@/views/HostsView.vue";

const HuntReportsListView = () => import('@/views/HuntReportsListView.vue')
const ReportView = () => import('@/views/ReportView.vue')
const FindingReportRequestView = () =>
  import('@/views/FindingReportRequestView.vue')

const LogOut = () => import('@/views/LogOut.vue')
const MitreAttackGraph = () => import('@/views/MitreAttackMatrix.vue')
const AccessDenied = () => import('@/views/AccessDenied.vue')
const NotFound = () => import('@/views/NotFound.vue')
const Redirect = () => import('@/views/Redirect.vue')
const DeploymentView = () => import('@/views/Deployment/DeploymentView.vue')
const UsersView = () => import('@/views/Platform/UsersView.vue')
const PlatformSettingsView = () =>
  import('@/views/Platform/PlatformSettingsView.vue')
const CustomizationsTab = () => import('@/views/Platform/CustomizationsTab.vue')
const NotificationsTab = () => import('@/views/Platform/NotificationsTab.vue')
const PCAPUploadView = () => import('@/views/PCAPUploadView.vue')

/**
 * Settings section
 */
const SettingsView = () => import('@/views/Settings/SettingsView.vue')
// Sub-sections
const Account = () => import('@/views/Settings/AccountView.vue')
const ContactFormVue = () => import('@/views/ContactFormView.vue')
const SlackIntegrationView = () =>
  import('@/views/Settings/SlackIntegrationView.vue')

/**
 * Info cards
 */
const APIPlaygroundInfoCard = defineAsyncComponent(
  () => import('@/components/displays/info/APIPlaygroundInfoCard.vue'),
)
const AssetInfoCard = defineAsyncComponent(
  () => import('@/components/displays/info/AssetInfoCard.vue'),
)
const DeploymentInfoCard = defineAsyncComponent(
  () => import('@/components/displays/info/DeploymentInfoCard.vue'),
)
const FindingInfoCard = defineAsyncComponent(
  () => import('@/components/displays/info/FindingInfoCard.vue'),
)
const MitreInfoCard = defineAsyncComponent(
  () => import('@/components/displays/info/MitreInfoCard.vue'),
)
const NetworkEventsInfoCard = defineAsyncComponent(
  () => import('@/components/displays/info/NetworkEventsInfoCard.vue'),
)
const PCAPUploadInfoCard = defineAsyncComponent(
  () => import('@/components/displays/info/PCAPUploadInfoCard.vue'),
)
const PlatformSettingsInfoCard = defineAsyncComponent(
  () => import('@/components/displays/info/PlatformSettingsInfoCard.vue'),
)
const ReportsInfoCard = defineAsyncComponent(
  () => import('@/components/displays/info/ReportsInfoCard.vue'),
)
const UsersInfoCard = defineAsyncComponent(
  () => import('@/components/displays/info/UsersInfoCard.vue'),
)

const router = createRouter({
  history: createWebHistory(getEnv('BASE_URL')),
  routes: [
    {
      path: '/',
      name: 'home',
      redirect: { name: 'dashboard' },
      meta: {
        title: 'Home',
      },
    },
    {
      // Allow for closed
      path: '/dashboard',
      name: 'dashboard',
      component: Dashboard,
      meta: {
        title: 'Dashboard',
      },
    },
    // TODO: DNS app
    {
      // Allow for closed
      path: '/findings',
      name: 'findings',
      component: Findings,
      meta: {
        title: 'Findings',
        infoCard: FindingInfoCard,
      },
      children: [
        {
          path: ':id',
          name: 'findings:detail',
          component: DetailedFinding,
          props: true,
          meta: {
            title: 'Finding detail',
            // hideTitle: true,
          },
        },
      ],
    },
    {
      path: '/network-events',
      name: 'network-events',
      component: FindingsNetworkEventsTab,
      meta: {
        title: 'Network Events',
        requiredCapability: 'viewNetworkEvents',
        infoCard: NetworkEventsInfoCard,
      },
    },
    {
      path: '/assets',
      name: 'assets',
      component: AssetsListView,
      // Default to the Map tab when the user navigates to /assets.
      redirect: { name: 'asset:all:map' },
      // The asset filter schema builds one field per custom asset attribute definition,
      // synchronously, the moment a view under /assets mounts — before that, definitions
      // wouldn't be loaded yet (the definitions store otherwise only fetches on its own
      // onMounted, which runs after the schema is already built). Prefetching here ensures
      // they're loaded before any child route mounts.
      beforeEnter: async (to, from, next) => {
        await useCustomAssetAttributeDefinitionsStore().ensureLoaded()
        next()
      },
      meta: {
        title: 'Assets',
        infoCard: AssetInfoCard,
      },
      children: [
        {
          path: 'map',
          name: 'asset:all:map',
          component: AllCommunicationsViz,
          meta: {
            title: 'Network Map',
            // Hide from the breadcrumb header (the tab bar already
            // labels the active tab; we don't want "Assets > Map").
            hideTitle: true,
          },
        },
        {
          path: 'list',
          name: 'asset:all:list',
          component: { render: () => null },
          meta: {
            title: 'List',
            hideTitle: true,
          },
        },
        {
          path: 'lapses',
          name: 'asset:lapses',
          component: AssetLapseTimelineView,
          meta: {
            title: 'Communication lapses',
          },
        },
        {
          path: 'network-activity',
          name: 'asset:network-activity',
          component: AssetUptimeHistoryView,
          meta: {
            title: 'Network Activity',
            hideTitle: true,
          },
        },
        {
          path: 'all/communication',
          name: 'asset:all:visualization',
          // Back-compat: keep the old URL working by redirecting to the
          // new Map tab. Anything bookmarked here still resolves.
          redirect: { name: 'asset:all:map' },
          meta: {
            title: 'All Communication',
          },
        },
        {
          path: ':id',
          name: 'asset:detail',
          component: AssetDetailWrapper,
          props: true,
          redirect: (to) => ({
            name: 'asset:detail:details',
            params: { id: to.params.id },
          }),
          meta: {
            title: 'Asset detail',
            finalTopLevelBreadcrumb: true,
            hasDynamicTitle: true,
          },
          children: [
            {
              path: '',
              name: 'asset:detail:details',
              component: AssetDetailsTab,
              meta: {
                title: 'Asset details',
                hideTitle: true,
              },
            },
            {
              path: 'communications',
              name: 'asset:detail:communications',
              component: AssetCommsTimelineView,
              props: true,
              meta: {
                title: 'Communications',
              },
            },
            {
              path: 'inbound',
              name: 'asset:detail:inbound',
              component: AssetCommsView,
              props: { direction: 'inbound' },
              meta: {
                title: 'Inbound communication',
              },
            },
            {
              path: 'outbound',
              name: 'asset:detail:outbound',
              component: AssetCommsView,
              props: { direction: 'outbound' },
              meta: {
                title: 'Outbound communication',
              },
            },
            {
              path: 'services',
              name: 'asset:detail:services',
              component: AssetServicesView,
              meta: {
                title: 'Network services',
              },
            },
            {
              path: 'processes',
              name: 'asset:detail:processes-wrapper',
              props: true,
              meta: {
                title: 'Processes',
              },
              children: [
                {
                  path: '',
                  name: 'asset:detail:processes',
                  component: AssetProcessesView,
                  props: true,
                  meta: {
                    title: 'Processes',
                  },
                },
                {
                  path: ':process',
                  name: 'asset:detail:process-executions',
                  component: AssetProcessExecutionsForProcess,
                  props: true,
                  meta: {
                    title: 'Process executions',
                  },
                },
                {
                  path: 'tree/:guid',
                  name: 'asset:detail:process-tree',
                  props: true,
                  component: ProcessTreeView,
                  beforeEnter: (to, from, next) => {
                    const guid = to.params.guid as string
                    const guidLC = guid.toLowerCase()
                    if (guid !== guidLC) {
                      next({
                        name: to.name,
                        params: {
                          ...to.params,
                          guid: guid && guid.toLowerCase(),
                        },
                        query: to.query,
                      })
                    } else {
                      next()
                    }
                  },
                  meta: {
                    title: 'Process tree',
                  },
                },
              ],
            },
            {
              path: 'visualization',
              name: 'asset:detail:visualization',
              component: AssetVisualizationView,
              props: { show: true },
              meta: {
                title: 'Asset communications visualization',
              },
            },
            {
              path: 'findings',
              name: 'asset:detail:findings',
              props: { isEmbedded: true },
              component: AssetFindingsView,
              meta: {
                title: 'Asset-involved findings',
              },
            },
          ],
        },
      ],
    },
    {
      path: '/reports',
      name: 'reports',
      redirect: (_) => ({ name: 'reports:findings' }),
      component: ReportView,
      meta: {
        title: 'Reports',
        infoCard: ReportsInfoCard,
        finalTopLevelBreadcrumb: true,
      },
      children: [
        {
          path: 'findings',
          name: 'reports:findings',
          component: FindingReportRequestView,
          meta: {
            title: 'Findings summary',
            hideTitle: true,
            requiredCapability: 'reportsFindingsPost',
            deniedRedirect: 'reports:hunt',
          },
        },
        {
          path: 'hunts',
          name: 'reports:hunt',
          component: HuntReportsListView,
          meta: {
            title: 'Hunt reports',
            hideTitle: true,
          },
        },
      ],
    },
    {
      path: '/mitre',
      name: 'mitre',
      component: MitreAttackGraph,
      meta: {
        title: 'MITRE ATT&CK',
        infoCard: MitreInfoCard,
      },
    },
    {
      path: '/playground',
      name: 'api',
      component: APIPlaygroundView,
      meta: {
        title: 'API',
        infoCard: APIPlaygroundInfoCard,
      },
    },
    {
      // `:id?` rather than a child route so selecting a run changes params
      // without remounting the workspace, which would discard the editor buffer
      // and re-create Monaco.
      path: '/anvil/:id?',
      name: 'anvil',
      component: AnvilView,
      meta: {
        title: 'Anvil',
        hideTitle: true,
        fullWidth: true,
      },
    },
    {
      path: '/platform/deployment',
      name: 'platform:deployment',
      component: DeploymentView,
      meta: {
        title: 'Deployment',
        requiredCapability: 'manageDeploymentSettings',
        infoCard: DeploymentInfoCard,
        finalTopLevelBreadcrumb: true,
      },
    },
    {
      path: '/platform/users',
      name: 'platform:users',
      component: UsersView,
      meta: {
        title: 'Users',
        requiredCapability: 'manageUsers',
        infoCard: UsersInfoCard,
        finalTopLevelBreadcrumb: true,
      },
    },
    {
      path: '/platform/settings',
      name: 'platform:settings',
      component: PlatformSettingsView,
      redirect: { name: 'platform:settings:notifications' },
      meta: {
        title: 'Settings',
        requiredCapability: 'managePlatformSettings',
        infoCard: PlatformSettingsInfoCard,
        finalTopLevelBreadcrumb: true,
      },
      children: [
        {
          path: 'notifications',
          name: 'platform:settings:notifications',
          component: NotificationsTab,
          meta: {
            title: 'Notifications',
            requiredCapability: 'manageNotificationsSettings',
            hideTitle: true,
          },
        },
        {
          path: 'customizations',
          name: 'platform:settings:customizations',
          component: CustomizationsTab,
          meta: {
            title: 'Customizations',
            requiredCapability: 'manageCustomAssetAttributes',
            hideTitle: true,
          },
        },
      ],
    },
    {
      path: '/tools/pcap-uploads',
      name: 'tools:pcap_uploads',
      component: PCAPUploadView,
      meta: {
        title: 'PCAP Upload',
        requiredCapability: 'uploadPCAPs',
        infoCard: PCAPUploadInfoCard,
        finalTopLevelBreadcrumb: true,
      },
    },
    {
      path: '/settings',
      name: 'settings',
      component: SettingsView,
      redirect: (_) => ({ name: 'settings:account' }),
      meta: {
        title: 'Account',
        icon: UserIcon,
        finalTopLevelBreadcrumb: true,
      },
      children: [
        {
          path: '/account',
          name: 'settings:account',
          component: Account,
          meta: {
            hideTitle: true,
            title: 'Profile',
          },
        },
        {
          path: '/integrations/slack',
          name: 'settings:integrations:slack',
          component: SlackIntegrationView,
          meta: {
            hideTitle: true,
            title: 'Slack Integration',
          },
        },
        {
          path: '/contact',
          name: 'settings:contact-form',
          component: ContactFormVue,
          meta: {
            hideTitle: true,
            title: 'Contact Galvanick',
            requiredCapability: 'contactPost',
          },
        },
      ],
    },
    // Pre-sidebar URLs, kept working for bookmarks.
    { path: '/ids', redirect: { name: 'network-events' } },
    { path: '/admin', redirect: { name: 'platform:deployment' } },
    { path: '/deployment', redirect: { name: 'platform:deployment' } },
    { path: '/admin/settings', redirect: { name: 'platform:deployment' } },
    { path: '/admin/users', redirect: { name: 'platform:users' } },
    {
      path: '/admin/customizations',
      redirect: { name: 'platform:settings:customizations' },
    },
    {
      path: '/admin/notifications',
      redirect: { name: 'platform:settings:notifications' },
    },
    {
      path: '/admin/pcap_uploads',
      redirect: { name: 'tools:pcap_uploads' },
    },
    {
      path: '/logout',
      name: 'logout',
      component: LogOut,
      meta: {
        title: 'Logging out',
        noLayout: true,
      },
    },
    {
      path: '/redirect',
      name: 'redirect',
      component: Redirect,
      meta: {
        title: 'Re-direct',
        noLayout: true,
      },
    },
    {
      path: '/403',
      name: '403',
      component: AccessDenied,
      meta: {
        title: 'Access denied',
        hideTitle: true,
      },
    },
    {
      path: '/404',
      name: '404',
      component: NotFound,
      meta: {
        title: 'Page not found',
        noLayout: true,
      },
    },
    {
      path: '/no-organization',
      name: 'no-organization',
      component: () => import('@/views/NoOrganization.vue'),
      props: { reason: 'organization' },
      meta: {
        title: 'No organization',
        noLayout: true,
      },
    },
    {
      path: '/no-role',
      name: 'no-role',
      component: () => import('@/views/NoOrganization.vue'),
      props: { reason: 'role' },
      meta: {
        title: 'No role assigned',
        noLayout: true,
      },
    },
    {
      path: '/account-disabled',
      name: 'account-disabled',
      component: () => import('@/views/AccountDisabled.vue'),
      meta: {
        title: 'Account disabled',
        noLayout: true,
      },
    },
  ],
})

router.beforeEach(async (to, from, next) => {
  // @ts-ignore
  if (to.matched.length === 0) {
    // No matching route found; Go to 404
    return next({ path: '/404', query: { path: to.path } }) // Redirect to a 404 page
  } else {
    if (to.meta?.title && typeof to.meta?.title === 'string') {
      updateTitle(to.meta.title)
    }

    /*
      The pages that explain why the portal is closed to someone are reachable
      only while that is true of the caller.
    */
    if (isBlockedPage(to.name)) {
      const authStore = useAuthStore()
      const allowed = canSeeBlockedPage(to.name, {
        hasNoOrganization: authStore.hasNoOrganization,
        hasNoValidRole: authStore.hasNoValidRole,
        isAccountDisabled: accountIsDisabled.value,
        isLoggedIn: authStore.isLoggedIn,
      })
      if (!allowed) {
        return next({ name: 'home' })
      }
    }

    // Check the user has valid organization and roles claims.
    // If they are missing either, show a default error page and block
    // access to the rest of the app. Without a valid role, all API calls
    // will fail with 403. No organization membership is less bad. A read-only
    // or user role would be fine, but an admin would not be able to
    // fetch users.
    if (!to.meta.noLayout) {
      const authStore = useAuthStore()
      if (authStore.hasNoOrganization) {
        return next({ name: 'no-organization' })
      }
      if (authStore.hasNoValidRole) {
        return next({ name: 'no-role' })
      }
    }
    const denied = capabilityRedirect(to, useAuthStore())
    if (denied) {
      return next(denied)
    }
    return next() // Proceed to the matched route
  }
})

export default router
